import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import matterService from '@/services/matter/matterService';
import userService from '@/services/setting/userService';
import { useAbility } from '@/providers/';
import { toast } from 'sonner';
import { KeenIcon } from '@/components';
import { Can } from '@casl/react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { useAuthContext } from '@/auth';
import { Link, useNavigate } from 'react-router-dom';
import { FiltersToFormData, getAccessToken, renderMultiAvatars, decodeHtmlEntities } from '@/utils'
import { createDataTableOptions, createDataTableConfig } from '@/utils';
import { ConfirmationModal, PaymentModal } from '@/components/modal';
import { useConfirmationModal, useContactOptions, useMatterOptions } from '@/hooks';
import { SelectInput, DateInput, STATUS_INVOICE_LISTING_OPTIONS, FilterContainer, ColumnToggleDropdown } from '@/components/TableFilter'
import { cn } from '@/lib/utils';
import invoiceService from '@/services/billing/invoice/invoiceService';
import bulkDeleteService from '@/services/bulk-delete/bulkDeleteService';
import { InvoiceBillingSummary } from '@/components/invoice';
import { goTo403 } from "@/utils/Navigation";
import { Check, CircleDollarSign, Send } from 'lucide-react'
import { SettlementEditor, SettlementEditorModal } from '@/components/invoice';
import FixedHeader from 'datatables.net-fixedheader-dt';
import { useUserOptions } from '@/hooks'
import useDataTableBulkDelete from '@/hooks/useDataTableBulkDelete';
import { BulkDeleteActions } from '@/components/BulkDeleteActions';
import { Container } from '@/components/container';

DataTable.use([DT, FixedHeader]);

const badgeClasses = {
  billed: 'badge-success',
  approved: 'badge-success',
  paid: 'badge-success',
  pending: 'badge-warning',
};

const Invoice = () => {
  const { logout } = useAuthContext();
  const loadContactOptions = useContactOptions({ defaultPageSize: 20 });
  const loadMatterOptions = useMatterOptions({ defaultPageSize: 20 });
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [selectedData, setSelectedData] = useState(null);

  const [itemId, setItemId] = useState(null)
  const { ability } = useAbility();
  const [showFilter, setShowFilter] = useState(false);
  const [filterData, setFilterData] = useState({
    approved_by: null,
    invoice_status: null,
    payment_status: null,
    contact_id: null,
    matter_id: null,
    from_date: null,
    to_date: null,
    date_range: null,
  });
  const API_URL = import.meta.env.VITE_APP_BASE_URL;
  const token = getAccessToken();
  const tableRef = useRef(null);
  const filterRef = useRef(filterData);
  const formikRef = useRef();
  const canDeleteInvoice = ability.can('delete', 'invoice');
  const canEditInvoice = ability.can('handle', 'invoice');
  const canApproveInvoice = ability.can('approve', 'invoice');
  const Navigate = useNavigate()
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();
  const [invoicePaymentHistory, setInvoicePaymentHistory] = useState(0.00)
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false)
  const [offlinePaymentModal, setOfflinePaymentModal] = useState(false)
  const [offlinePaymentLoading, setOfflinePaymentLoading] = useState(false)
  const [paymentRows, setPaymentRows] = useState([]);
  const [isSettlementModalOpen, setIsSettlementModalOpen] = useState(false)
  const loadUserOptions = useUserOptions({ defaultPageSize: 20 });

  const headerCheckboxId = 'invoice-table-select-all';

  const syncHeaderCheckbox = useCallback(() => {
    const table = tableRef?.current?.dt?.();
    if (!table) return;
    const headerCheckbox = document.getElementById(headerCheckboxId);
    if (!headerCheckbox) return;
    const totalOnPage = table.rows({ page: 'current' }).count();
    const selectedOnPage = table.rows({ selected: true, page: 'current' }).count();
    headerCheckbox.checked = totalOnPage > 0 && totalOnPage === selectedOnPage;
    headerCheckbox.indeterminate = selectedOnPage > 0 && selectedOnPage < totalOnPage;
  }, [tableRef]);

  // Reflect each row's selection state on its (display-only) checkbox input.
  const syncRowCheckboxes = useCallback(() => {
    const table = tableRef?.current?.dt?.();
    if (!table) return;
    table.rows({ page: 'current' }).every(function () {
      const node = this.node();
      if (!node) return;
      const input = node.querySelector('.row-select-checkbox');
      if (input) input.checked = node.classList.contains('selected');
    });
  }, [tableRef]);

  const setupHeaderCheckbox = useCallback((api) => {
    const headerCell = api.column('checkbox:name').header();
    if (!headerCell) return;
    headerCell.innerHTML = `
      <label class="inline-flex items-center cursor-pointer">
        <input id="${headerCheckboxId}" type="checkbox" class="checkbox checkbox-sm cursor-pointer form-checkbox" />
      </label>
    `;
    const headerCheckbox = headerCell.querySelector(`#${headerCheckboxId}`);
    if (headerCheckbox) {
      headerCheckbox.addEventListener('change', (event) => {
        event.stopPropagation();
        const table = tableRef?.current?.dt?.();
        if (!table) return;
        if (event.target.checked) {
          table.rows({ page: 'current' }).select();
        } else {
          table.rows({ page: 'current' }).deselect();
        }
        syncHeaderCheckbox();
      });
    }
  }, [syncHeaderCheckbox, tableRef]);

  const refresh = useCallback(() => {
    const table = tableRef.current?.dt?.();
    if (!table) return;
    table.ajax.reload();
  }, []);

  const fetchInvoicePaymentHistory = useCallback(async () => {
    setPaymentHistoryLoading(true)
    try {
      const { data, status } = await invoiceService.getInvoicePaymentDetails();
      if (status) {
        console.log('data', data)
        setInvoicePaymentHistory(data)
      }
    } catch (error) {
      console.error('Error', error)
      toast.error('Failed to fetch Invoice Payment History.', {
        description: 'An error occurred while fetching invoice payment history. Please try again later.',

      });
    } finally {
      setPaymentHistoryLoading(false)
    }
  }, [])

  const {
    selectedRows,
    allSelected,
    bulkDeleteLoading,
    bulkDeleteOpen,
    openBulkDelete,
    closeBulkDelete,
    confirmBulkDelete,
    toggleSelectPage,
  } = useDataTableBulkDelete({
    tableRef,
    model: 'Invoice',
    deleteFn: bulkDeleteService.bulkDelete,
    onSuccess: () => {
      refresh();
      fetchInvoicePaymentHistory();
      toast.success('Invoices Deleted', {
        description: 'Selected invoices deleted successfully and moved to the recycle bin.',
      });
    },
    onError: (error) => {
      console.error('Error', error)
      if (error?.message == "Can't delete: Matter records are related to this Invoice.") {
        toast.warning('Failed to delete Invoice.', {
          description: `Can't delete: Matter records are related to this Invoice.`,
        });
        return;
      }
      toast.error('Failed to delete Invoice.', {
        description: error?.message || 'An error occurred while deleting invoices. Please try again later.',
      });
    },
  });

  useEffect(() => {
    filterRef.current = filterData;
  }, [filterData]);

  const paymentStatusClass = {
    pending: 'badge-warning',
    paid: 'badge-success',
    partially_paid: 'badge-info',
  }

  const handleNavigateConatct = useCallback((id) => {
    Navigate(`/contacts/${id}/`);
  }, [Navigate]);

  const handlePreviewInvoice = useCallback((id) => {
    Navigate(`/billing/invoice/preview/${id}`);
  }, [Navigate]);

  const handleNavigateMatter = useCallback((id) => {
    Navigate(`/matters/detail/${id}/`);
  }, [Navigate]);

  const renderNumber = useCallback((row) => {
    const fullNumber = `INVOICE-${row.number}`;
    const { id, is_import } = row;
    return (
      <span onClick={() => { handlePreviewInvoice(id); }} className='cursor-pointer link'>{fullNumber}</span>
    );
  }, [handlePreviewInvoice]);

  const renderContactName = useCallback((row) => {
    if (!row.contact) {
      return <span>-</span>;
    }

    const firstName = row.contact.first_name || '';
    const middleName = row.contact.middle_name || '';
    const lastName = row.contact.last_name || '';
    const fullName = `${firstName} ${middleName} ${lastName}`.trim();
    const { id } = row.contact;

    return (
      <span onClick={() => { handleNavigateConatct(id); }} className='cursor-pointer link'>
        {fullName || 'No Name Available'}
      </span>
    );
  }, [handleNavigateConatct]);

  const renderMatterName = useCallback((row) => {
    const { matter } = row;
    const fullName = decodeHtmlEntities(matter?.name || '');

    return (
      <span
        onClick={() => handleNavigateMatter(matter?.id || '')}
        className="cursor-pointer link"
      >
        {fullName}
      </span>
    );
  }, [handleNavigateMatter]);

  const handleSendInvoicetoContact = useCallback(async (id, contact) => {
    try {
      const { status } = await invoiceService.sendInvoiceToContact(id);
      if (status) {
        toast.success('Invoice Sent Successfully.', {
          description: 'Invoice has been sent to contact successfully.',
        });
        refresh();
        fetchInvoicePaymentHistory();
      }
    } catch (error) {
      console.error('Error', error);
      toast.error('Failed to send invoice to contact.', {
        description: 'An error occurred while sending invoice to contact. Please try again later.',
      });
    }
  }, [fetchInvoicePaymentHistory, refresh]);

  const handleApprove = useCallback(async (id) => {
    try {
      setLoading(true);
      const { data, status } = await invoiceService.aprroveInvoice(id);
      if (status) {
        refresh();
        fetchInvoicePaymentHistory();
        toast.success('Approved Successfully.', {
          description: 'Invoice Approved Successfully.',
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to approve Invoice.', {
        description: 'An error occurred while approving invoice. Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  }, [fetchInvoicePaymentHistory, refresh]);

  const handleEdit = useCallback((id) => {
    Navigate(`/billing/invoice/edit/${id}/`);
  }, [Navigate]);

  const handleClone = useCallback((id) => {
    Navigate(`/billing/invoice/create/${id}/`);
  }, [Navigate]);

  const renderCustomActions = useCallback((row) => {
    const { id, payment_status, invoice_status, is_approval_required, contact, is_import } = row;

    // Imported invoices have no line items: they can be paid, sent, approved or deleted, not edited or cloned (OL-145).
    const isImported = is_import === 1;
    const isEditable =
      (canEditInvoice) ||
      (canApproveInvoice && invoice_status === "approved");
    const showPaymentButtons =
      canEditInvoice &&
      (invoice_status === 'approved' ||
        (invoice_status === 'billed' && payment_status !== 'paid'));
    return (
      <DropdownMenu>
        {(canEditInvoice) && (
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="text-gray-600">
              <KeenIcon icon="dots-vertical" className="text-[18px]" />
            </Button>
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent align="end" className="w-40">
          {/* {showPaymentButtons && (
            <DropdownMenuItem onClick={() => {
              setPaymentRows([row]);
              setOfflinePaymentModal(true);
            }}>
              <CircleDollarSign className="mr-0.5 h-4 w-4 cursor-pointer" />
              Pay Invoice
            </DropdownMenuItem>
          )} */}
          {showPaymentButtons && (
            <DropdownMenuItem onClick={() => {
              setPaymentRows([row]);
              setOfflinePaymentModal(true);
            }}>
              <KeenIcon icon="bill" className="mr-0.5 h-4 w-4 cursor-pointer" /> Pay Invoice
            </DropdownMenuItem>
          )}
          {canEditInvoice && invoice_status !== 'paid' && payment_status !== 'paid' && !(invoice_status === 'pending' && Number(is_approval_required) === 1) && (
            <DropdownMenuItem onClick={() => {
              if (!contact?.email?.trim()) {
                toast.warning('Missing Email Address', {
                  description: 'This contact has no email. Please add one before proceeding.',
                });
                return;
              }
              handleSendInvoicetoContact(id, contact);
            }}>
              <Send className="mr-0.5 h-4 w-4 cursor-pointer" /> Send Invoice
            </DropdownMenuItem>
          )}
          {canApproveInvoice && invoice_status === 'pending' && payment_status === 'pending' && is_approval_required === 1 && (
            <DropdownMenuItem onClick={() => handleApprove(id)}>
              <KeenIcon icon="archive-tick" className="mr-0.5 h-4 w-4 cursor-pointer" /> Approve
            </DropdownMenuItem>
          )}
          {!isImported && (<Can I="handle" a="invoice" ability={ability}>
            <DropdownMenuItem onClick={() => handleClone(id)}>
              <KeenIcon icon="note-2" className="mr-0.5 h-3.5 w-4 cursor-pointer" /> Clone
            </DropdownMenuItem>
          </Can>)}
          {isEditable && !isImported && (
            <DropdownMenuItem onClick={() => handleEdit(id)}>
              <KeenIcon icon="notepad-edit" className="mr-0.5 h-4 w-4 cursor-pointer" /> Edit
            </DropdownMenuItem>
          )}
          {canDeleteInvoice && (
            <DropdownMenuItem onClick={() => openModal(id)} className="text-red-600 cusror-pointer">
              <KeenIcon icon="trash" className="mr-0.5 h-4 w-4 cursor-pointer" /> Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }, [
    ability,
    canApproveInvoice,
    canDeleteInvoice,
    canEditInvoice,
    handleApprove,
    handleClone,
    handleEdit,
    handleSendInvoicetoContact,
    openModal,
  ]);

  const columns = useMemo(() => ([
    // {
    //   title: 'S#',
    //   name: 'serial',
    //   data: null,
    //orderable: true,se,
    //   render: (data, type, row, meta) => meta.row + 1,
    // },
    {
      data: null,
      defaultContent: '',
      className: 'dt-row-select cursor-pointer text-center',
      id: 'invoice-checkbox-select',
      orderable: false,
      name: 'checkbox',
      render: function () {
        // Real styled checkbox so row selection matches the header "select all"
        // checkbox. pointer-events-none lets the click fall through to the cell,
        // which DataTables Select uses to toggle selection (see select.selector).
        return '<input type="checkbox" tabindex="-1" aria-label="Select row" class="checkbox checkbox-sm form-checkbox row-select-checkbox pointer-events-none" />';
      },
    },
    {
      data: 'contact',
      title: 'Contact',
      name: 'contact',
      orderable: true,
      render: function (data, type, row) {
        return `<div id="contact-cell-${row.id}"></div>`;
      }
    },
    {
      data: 'matter',
      title: 'Matter',
      name: 'matter',
      orderable: true,
      render: function (data, type, row) {
        return `<div id="matter-cell-${row.id}"></div>`;
      }
    },
    {
      data: 'number',
      title: 'Invoice Number',
      name: 'number',
      orderable: true,
      render: function (data, type, row) {
        return `<div id="number-cell-${row.id}"></div>`;
      }
    },
    {
      data: 'is_import',
      title: 'Imported Invoice',
      name: 'is_import',
      render: function (value) {
        console.log(value)
        const is_import = value === 1 ? 'Imported' : '-';
        if (value === 0) return is_import;
        return `<span class="capitalize badge badge-pill badge-outline badge-info gap-1 items-center">
        <span class="badge badge-dot size-1.5 badge-info"></span>
        ${is_import}
      </span>`;
      }
    },
    {
      data: 'total',
      title: 'Total',
      name: 'total',
      render: function (data, type, row) {
        return `<div>$${row.total}</div>`;
      }
    },
    {
      data: 'partial_payment_amount',
      title: 'Paid Amount',
      name: 'partial_payment_amount',
      render: function (data, type, row) {
        if (row.partial_payment_amount === null) return '-';
        return `<div>$${row.partial_payment_amount}</div>`;
      }
    },
    {
      data: 'settlements',
      title: 'Settlement Amount',
      name: 'settlements',
      orderable: false,
      render: function (data, type, row) {
        return `<div id="settlement-cell-${row.id}"></div>`;
      },
    },
    {
      data: 'invoice_status',
      title: 'Invoice Status',
      name: 'invoice_status',
      render: function (value) {
        if (!value) return '-';
        return `<span class="capitalize badge badge-pill badge-outline ${badgeClasses[value]} gap-1 items-center">
        <span class="badge badge-dot size-1.5 ${badgeClasses[value]}"></span>
        ${value}
      </span>`;
      }
    },
    {
      data: 'payment_status',
      title: 'Payment Status',
      name: 'payment_status',
      render: function (value) {
        if (!value) return '-';
        return `<span class="capitalize badge badge-pill badge-outline ${paymentStatusClass[value]} gap-1 items-center">
        <span class="badge badge-dot size-1.5 ${paymentStatusClass[value]}"></span>  
        ${value.split('_').join(' ')}
      </span>`;
      }
    },
    {
      data: 'invoice_age',
      title: 'Aging',
      name: 'invoice_age',
      orderable: false,
      render: function (data, type, row) {
        if (row.invoice_age === null) return '-';
        return `<div>${row.invoice_age}</div>`;
      }
    },
    { data: 'date', title: 'Date', name: 'date' },
    {
      data: 'payment_note',
      title: 'Payment Note',
      name: 'payment_note',
      render: function (data, type, row) {
        return `<div>${data || '-'}</div>`;
      }
    },
    {
      data: 'approved_by',
      title: 'Approved By',
      name: 'approved_by',
      orderable: false,
      render: function (data, type, row) {
        return `<div id="user-cell-${row.id}"></div>`;  // Using string template for HTML
      }
    },
    // {
    //   data: 'settlements',
    //   title: 'Settlement Amount',
    //   name: 'settlements',
    //   orderable: true,
    //   render: function (data, type, row) {
    //     const amount =
    //       Array.isArray(row.settlements) && row.settlements.length > 0
    //         ? row.settlements[0].amount
    //         : null;
    //     return `<div>${amount ? `$${amount}` : '-'}</div>`;
    //   },
    // },



    {
      data: 'created_at',
      title: 'Created At',
      name: 'created_at',
      orderable: true,
    },

    // --- Extended Columns Below (New Keys) ---
    // { data: 'id', title: 'ID', name: 'id' },
    { data: 'due_date', title: 'Due Date', name: 'due_date' },
    { data: 'discount_percent', title: 'Discount %', name: 'discount_percent' },
    { data: 'total_discount', title: 'Total Discount', name: 'total_discount' },
    {
      data: 'terms_and_conditions',
      title: 'Terms & Conditions',
      name: 'terms_and_conditions',
      render: (data) => data ? data.replace(/<\/?[^>]+(>|$)/g, '') : '-'
    },
    {
      data: 'terms_and_conditions_note',
      title: 'Terms & Conditions Note',
      name: 'terms_and_conditions_note',
      render: (data) => data ? data.replace(/<\/?[^>]+(>|$)/g, '') : '-'
    },
    // { data: 'submitted_at', title: 'Submitted At', name: 'submitted_at' },
    // { data: 'approved_at', title: 'Approved At', name: 'approved_at' },
    // { data: 'send_payment_reminder', title: 'Send Payment Reminder', name: 'send_payment_reminder' },
    // { data: 'accept_online_payments', title: 'Accept Online Payments', name: 'accept_online_payments' },
    // { data: 'updated_at', title: 'Updated At', name: 'updated_at' },
    { data: 'sub_total', title: 'Sub Total', name: 'sub_total' },
    { data: 'total_tax', title: 'Total Tax', name: 'total_tax' },
    { data: 'billed_amount', title: 'Billed Amount', name: 'billed_amount', orderable: false },
    { data: 'due_amount', title: 'Due Amount', name: 'due_amount', orderable: false },

    {
      data: 'invoice_template',
      title: 'Invoice Template',
      name: 'invoice_template',
      render: (data, type, row) => row.invoice_template?.name || '-'
    },
    {
      data: 'created_by',
      title: 'Created By',
      name: 'created_by',
      render: (data, type, row) => row.created_by ? `${row.created_by.first_name || ''} ${row.created_by.last_name || ''}` : '-'
    },
    {
      data: 'expenses',
      title: 'Expenses',
      name: 'expenses',
      render: (data, type, row) => Array.isArray(row.expenses) && row.expenses.length
        ? row.expenses.map(e => `$${e.total}`).join(', ')
        : '-'
    },
    {
      data: 'time_entries',
      title: 'Time Entries',
      name: 'time_entries',
      render: (data, type, row) => Array.isArray(row.time_entries) && row.time_entries.length
        ? row.time_entries.map(e => `$${e.total}`).join(', ')
        : '-'
    },
    {
      data: 'flat_fees',
      title: 'Flat Fees',
      name: 'flat_fees',
      render: (data, type, row) => Array.isArray(row.flat_fees) && row.flat_fees.length
        ? row.flat_fees.map(e => `$${e.total}`).join(', ')
        : '-'
    },
    {
      title: 'Actions',
      data: null,
      orderable: false,
      searchable: false,
      render: function (data, type, row) {
        return `<div id="action-cell-${row.id}"></div>`;
      },
      name: 'actions',
    },

  ]), []);

  const defaultVisibleColumns = [
    // 'serial',
    'checkbox',
    'contact',
    'matter',
    'number',
    'is_import',
    'total',
    'partial_payment_amount',
    'settlements',
    'invoice_status',
    'payment_status',
    // 'date',
    // 'approved_by',
    'invoice_age',
    'created_at',
    'actions',
  ];


  const handleDelete = async () => {
    closeModal();
    try {
      const { status } = await invoiceService.deleteInvoice(actionId);
      if (status) {
        refresh();
        fetchInvoicePaymentHistory();
        toast.success('Deleted Invoice.', {
          description: 'Invoice deleted Successfully and moved to the recycle bin.',
        });
      }
    } catch (error) {
      console.error('Error', error)
      if (error.message == "Can't delete: Matter records are related to this Invoice.") {
        toast.warning('Failed to delete Expense.', {
          description: `Can't delete: Matter records are related to this Invoice.`,
        });
        return
      }
      toast.error('Failed to delete Invoice.', {
        description: 'An error occurred while deleting invoice. Please try again later.',

      });
    }
  }

  const handleModalClose = () => {
    setItemId(null)
    setSelectedData(null)
    setMode('create')
    setOpen(false)
  }

  const ajaxConfig = createDataTableConfig('/setup/invoices/data_table_list', filterRef, token, logout);

  const options = useMemo(() => createDataTableOptions({
    order: [[14, "desc"]],
    select: {
      style: 'multi',
      selector: 'td.dt-row-select'
    },
    initComplete: function () {
      const api = this.api();
      setupHeaderCheckbox(api);
      api.on('select deselect draw', () => {
        syncHeaderCheckbox();
        syncRowCheckboxes();
      });
      api.on('page', () => {
        api.rows().deselect();
      });
      api.columns().every(function () {
        const col = this;
        const name = col.settings()[0].aoColumns[col.index()].name;
        if (name && !defaultVisibleColumns.includes(name)) {
          col.visible(false);
        }
      });
      syncHeaderCheckbox();
    },
    drawCallback: function () {
      const api = this.api();
      const rows = api.rows({ page: 'current' }).data();
      syncHeaderCheckbox();
      syncRowCheckboxes();

      rows.each((row) => {
        const container = document.getElementById(`action-cell-${row.id}`);
        if (container) {
          if (!container._reactRoot) {
            container._reactRoot = createRoot(container);
          }
          container._reactRoot.render(renderCustomActions(row));
        }
        const contactContainer = document.getElementById(`contact-cell-${row.id}`);
        if (contactContainer) {
          if (!contactContainer._reactRoot) {
            contactContainer._reactRoot = createRoot(contactContainer);
          }
          contactContainer._reactRoot.render(renderContactName(row));
        }
        const matterContainer = document.getElementById(`matter-cell-${row.id}`);
        if (matterContainer) {
          if (!matterContainer._reactRoot) {
            matterContainer._reactRoot = createRoot(matterContainer);
          }
          matterContainer._reactRoot.render(renderMatterName(row));
        }
        const numberContainer = document.getElementById(`number-cell-${row.id}`);
        if (numberContainer) {
          if (!numberContainer._reactRoot) {
            numberContainer._reactRoot = createRoot(numberContainer);
          }
          numberContainer._reactRoot.render(renderNumber(row));
        }
        const userContainer = document.getElementById(`user-cell-${row.id}`);
        if (userContainer) {
          if (!userContainer._reactRoot) {
            userContainer._reactRoot = createRoot(userContainer);
          }
          userContainer._reactRoot.render(renderMultiAvatars(row.approved_by));
        }
        const settlementContainer = document.getElementById(`settlement-cell-${row.id}`);
        if (settlementContainer) {
          if (!settlementContainer._reactRoot) {
            settlementContainer._reactRoot = createRoot(settlementContainer);
          }

          settlementContainer._reactRoot.render(
            <SettlementEditorModal
              row={row} refresh={refresh} fetchPaymentHistory={fetchInvoicePaymentHistory}
            />
          );
        }

      });
    },
  }), [
    renderContactName,
    renderCustomActions,
    renderMatterName,
    renderNumber,
    setupHeaderCheckbox,
    syncHeaderCheckbox,
    syncRowCheckboxes,
  ]);

  const resetFilters = () => {
    const cleared = {
      approved_by: null,
      invoice_status: null,
      contact_id: null,
      matter_id: null,
      from_date: null,
      to_date: null,
      date_range: null,
    };

    setFilterData(cleared);
    filterRef.current = cleared;
    refresh()
  };

  const handleFilterChange = (key, val) => {
    setFilterData(prev => {
      const updated = { ...prev, [key]: val };
      if (key === 'contact_id') updated.matter_id = null; // recommended
      filterRef.current = updated;
      return updated;
    });
  };

  const handleToggleFilter = () => {
    setShowFilter(prev => !prev)
  }

  const fetchLookup = async (type, ctx = {}) => {
    switch (type) {
      case 'users': {
        const { data } = await userService.getAllUsers();
        return data.map(u => ({ label: `${u.first_name} ${u.last_name}`, value: u.id, email: u.email }));
      }
      case 'matter': {
        const contactId = ctx?.contactId || null;
        if (contactId) {
          const res = await invoiceService.getContactMatterRelatedData('contact', contactId);
          const rows = res?.data?.matters || [];
          return rows.map(m => ({ label: m.name, value: m.id }));
        }
        const matters = await matterService.getAllMatters();
        return (matters?.data || []).map(m => ({ label: m.name, value: m.id }));
      }
      default:
        return [];
    }
  };

  useEffect(() => {
    fetchInvoicePaymentHistory()
  }, [fetchInvoicePaymentHistory])

  const handleMakeOfflinePayment = async (values) => {
    console.log('paymentRows', paymentRows)
    setOfflinePaymentLoading(true)
    try {
      const payload = {
        model_id: paymentRows[0]?.contact?.id || null,
        model_type: 'contact',
        invoice_ids: paymentRows.map(inv => inv.id),
        settlement_price: values.settlement_price,
        payment_note: values.payment_note,
        is_settled: values.is_settled
      }
      // console.log('payload', payload);
      // return
      const { data, status } = await invoiceService.makeOfflineInvoicePayment(payload)
      if (status) {
        fetchInvoicePaymentHistory()
        refresh()
        handleCloseOfflinePaymentModal()
        toast.success('Invoice Paid Successfully.', {
          description: 'Invoice have been paid successfully.',
        })
      }
    } catch (error) {
      console.error('Error', error)
      toast.error('Failed to Make invoice Payments.', {
        description: 'An error occurred while making invoice payments. Please try again later.',
      })
    } finally {
      setOfflinePaymentLoading(false)
    }
  }

  const handleCloseOfflinePaymentModal = () => {
    setPaymentRows([])
    setOfflinePaymentModal(false)
  }

  return (
    <>
      <Container>
        <InvoiceBillingSummary paymentData={invoicePaymentHistory} loading={paymentHistoryLoading} />
        {/* Filters */}
        <FilterContainer
          show={showFilter}
          onApply={() => refresh()}
          onReset={resetFilters}
        >
          <SelectInput
            label="Approved By"
            type="users"
            value={filterData.approved_by}
            onChange={(val) => handleFilterChange('approved_by', val)}
            loadOptions={loadUserOptions}
            pageSize={20}
            customClass="sm:w-[32%] w-full"
          />

          <SelectInput
            label="Contact"
            type="contact"
            value={filterData.contact_id}
            onChange={(val) => handleFilterChange('contact_id', val)}
            loadOptions={loadContactOptions}
            pageSize={20}
            customClass="sm:w-[32%] w-full"
          />

          <SelectInput
            label="Matter"
            type="matter"
            value={filterData.matter_id}
            onChange={(val) => handleFilterChange('matter_id', val)}
            loadOptions={loadMatterOptions}
            pageSize={20}
            // NEW: pass contactId; when it changes, the component auto-refetches
            lookupParams={{ contactId: filterData.contact_id?.value ?? null }}
            customClass="sm:w-[32%] w-full"
          />

          <SelectInput
            label="Invoice Status"
            value={filterData.invoice_status}
            onChange={(val) => handleFilterChange('invoice_status', val)}
            staticOptions={STATUS_INVOICE_LISTING_OPTIONS.slice(0, -1)}
            customClass="sm:w-[32%] w-full"
          />

          <SelectInput
            label="Payment Status"
            value={filterData.payment_status}
            onChange={(val) => handleFilterChange('payment_status', val)}
            staticOptions={STATUS_INVOICE_LISTING_OPTIONS.slice(-3)}
            customClass="sm:w-[32%] w-full"
          />

          <DateInput
            customClass="sm:w-[32%] w-full"
            label="Created At - (From, To)"
            mode="range"
            value={filterData.date_range}
            onChange={(range) => {
              setFilterData(prev => ({
                ...prev,
                from_date: range?.from ?? null,
                to_date: range?.to ?? null,
                date_range: range,
              }));
            }}
          />
        </FilterContainer>

        <div className="min-w-full mb-4 card">
          <div className="card-header">
            <div className='flex items-center gap-1'>
              <ColumnToggleDropdown tableRef={tableRef} columns={columns} defaultVisibleColumns={defaultVisibleColumns} />
              {/* <h3 className="card-title">Invoices</h3> */}
            </div>

            <div className='flex flex-wrap items-center gap-2'>
              <BulkDeleteActions
                canDelete={canDeleteInvoice}
                selectedCount={selectedRows.length}
                bulkDeleteLoading={bulkDeleteLoading}
                allSelected={allSelected}
                onOpenConfirm={openBulkDelete}
                onToggleSelectPage={toggleSelectPage}
                confirmOpen={bulkDeleteOpen}
                onCloseConfirm={closeBulkDelete}
                onConfirmDelete={confirmBulkDelete}
                confirmMessage={`
                You are about to delete ${selectedRows.length} invoice(s).
                You can restore it from the Recycle Bin within 30 days.
              `}
              />
              <button className='text-xs btn btn-sm btn-primary btn-outline' onClick={handleToggleFilter}>
                <KeenIcon icon="filter" className="w-fit btn-icon" />
                {showFilter ? 'Hide Advanced Filters' : 'Advanced Filters'}
              </button>
              <Can I="handle" a="invoice" ability={ability}>
                <Link to='/billing/invoice/create' className='text-xs btn btn-sm btn-light'>
                  <KeenIcon icon="plus" className="w-fit btn-icon" />
                  Add New
                </Link>
              </Can>
            </div>
          </div>


          <DataTable
            className='table-with-checkbox'
            id="invoice-table"
            ajax={ajaxConfig}
            columns={columns}
            options={options}
            ref={tableRef}
          />

          <ConfirmationModal
            show={isOpen}
            onClose={closeModal}
            onConfirm={handleDelete}
            message="Are you sure you want to delete this Invoice? You can restore it from the Recycle Bin within 30 days."
            title="Are You Sure?"
          />

          {/* Offline Payment Modal */}
          <PaymentModal
            referesh={refresh}
            open={offlinePaymentModal}
            onConfirm={handleMakeOfflinePayment}
            onClose={handleCloseOfflinePaymentModal}
            invoiceData={paymentRows}
            loading={offlinePaymentLoading}
          />
        </div>
      </Container>
    </>
  )
}

export { Invoice }
